import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import i18n from "@/i18n";
import { INSTITUTION_LANGUAGE_KEY } from "../usePrintLanguage";
import { getAvailableLanguages } from "../service/languageService";
import type { LanguageOption } from "../service/languageService";

interface LanguageState {
  currentLanguage: string;
  availableLanguages: LanguageOption[];
  loading: boolean;
}

const initialState: LanguageState = {
  currentLanguage: localStorage.getItem("language") || "en",
  availableLanguages: [],
  loading: false,
};

export const fetchAvailableLanguages = createAsyncThunk(
  "language/fetchAvailable",
  async () => {
    return await getAvailableLanguages();
  }
);

const languageSlice = createSlice({
  name: "language",
  initialState,
  reducers: {
    /** The institute's language from its settings. Applies only while this user has not picked a language of their own. */
    applyInstitutionLanguage: (state, action: PayloadAction<string>) => {
      localStorage.setItem(INSTITUTION_LANGUAGE_KEY, action.payload);
      if (localStorage.getItem("language")) return;
      state.currentLanguage = action.payload;
      i18n.changeLanguage(action.payload);
    },
    /** The admin chose the institute language in Settings: it replaces any personal choice on this browser. */
    setInstitutionLanguage: (state, action: PayloadAction<string>) => {
      localStorage.setItem(INSTITUTION_LANGUAGE_KEY, action.payload);
      localStorage.removeItem("language");
      state.currentLanguage = action.payload;
      i18n.changeLanguage(action.payload);
    },
    setLanguage: (state, action: PayloadAction<string>) => {
      state.currentLanguage = action.payload;
      localStorage.setItem("language", action.payload);
      i18n.changeLanguage(action.payload);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAvailableLanguages.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchAvailableLanguages.fulfilled, (state, action) => {
        state.availableLanguages = action.payload;
        state.loading = false;
      })
      .addCase(fetchAvailableLanguages.rejected, (state) => {
        state.loading = false;
      });
  },
});

export const { setLanguage, applyInstitutionLanguage, setInstitutionLanguage } = languageSlice.actions;
export default languageSlice.reducer;